import { AbsoluteFill, Img, OffthreadVideo, getRemotionEnvironment } from 'remotion'
import { useCallback, useEffect, useState } from 'react'
import { isImageOnlyClip, type TimelineClip } from '@/lib/clipUtils'
import { secondsToFrames } from '../utils/framemath'
import { setClipFrame, clearClipFrame } from '../clipFrameStore'

// Persistent per-clip capture canvases. Extracted video frames (from
// OffthreadVideo) are copied here and used as the texture source, instead of
// the raw <img>: small <img> sources upload as black textures in the export's
// WebGL path, while a canvas of identical pixels renders correctly.
const captureCanvases = new Map<string, HTMLCanvasElement>()

interface ClipRendererProps {
  clip: TimelineClip
  fps: number
  volume?: number
  pauseWhenBuffering?: boolean
  muted?: boolean
  acceptableTimeShiftInSeconds?: number
}

/**
 * ClipRenderer — Single-path media renderer for both preview and export.
 *
 * Uses <OffthreadVideo> in preview (browser decoding) and <Video> in rendering
 * (Lambda). <Video> on Lambda uses Chrome's native decoder, avoiding ffmpeg
 * extraction issues that cause delayRender timeouts on Lambda workers.
 *
 * Frames are pushed to clipFrameStore via onVideoFrame. The Three.js layer
 * reads from the store — no DOM querying, no element-type branching.
 */
export function ClipRenderer({
  clip,
  fps,
  volume = 0,
  pauseWhenBuffering: pauseOnBuffer = true,
  muted = false,
  acceptableTimeShiftInSeconds,
}: ClipRendererProps) {
  const clipId = clip._id

  // Media-load failures (expired URL, CDN error, CORS) degrade this clip to
  // its poster image instead of cancelling the whole composition — without an
  // onError handler Remotion kills the Player and every clip goes black
  // (ISS-1231). During real renders the default throw is kept so exports fail
  // loudly rather than silently shipping poster frames.
  const { isRendering } = getRemotionEnvironment()
  const [videoFailed, setVideoFailed] = useState(false)
  const [posterFailed, setPosterFailed] = useState(false)

  // Clean up frame store on unmount
  useEffect(() => {
    return () => { clearClipFrame(clipId); captureCanvases.delete(clipId) }
  }, [clipId])

  const handleVideoError = useCallback((e: Error) => {
    console.error(`[ClipRenderer] video failed for clip ${clipId}, falling back to poster:`, e)
    clearClipFrame(clipId)
    setVideoFailed(true)
  }, [clipId])

  const handlePosterError = useCallback(() => {
    console.error(`[ClipRenderer] poster image failed for clip ${clipId}`)
    setPosterFailed(true)
  }, [clipId])

  // Demo scenes render in their own DOM layer above the compositor — no
  // frame source needed here.
  if (clip.demoScene) {
    return null
  }

  if (clip.solidColor) {
    return <AbsoluteFill style={{ backgroundColor: clip.solidColor }} />
  }

  const videoSrc = clip.videoUrl
  const hasVideo = clip.videoStatus === 'completed' && !!clip.videoUrl && !isImageOnlyClip(clip) && !videoFailed
  const startFromFrame = secondsToFrames(clip.trimStart || 0, fps)

  // onVideoFrame fires in both preview and rendering.
  // Push the frame to the store so ClipPlane can read it for texture capture.
  const handleVideoFrame = useCallback((frame: CanvasImageSource) => {
    let w = 0, h = 0
    if (frame instanceof HTMLVideoElement) {
      w = frame.videoWidth
      h = frame.videoHeight
    } else if (frame instanceof HTMLImageElement) {
      w = frame.naturalWidth
      h = frame.naturalHeight
    } else if (frame instanceof HTMLCanvasElement) {
      w = frame.width
      h = frame.height
    }
    if (w > 0 && h > 0) {
      // Live <video> (preview) is handed through directly for VideoTexture.
      // Everything else (the export path's extracted <img>) is copied into a
      // canvas we own — small <img> textures render black in the WebGL export.
      if (frame instanceof HTMLVideoElement) {
        setClipFrame(clipId, { source: frame, width: w, height: h })
      } else {
        let cv = captureCanvases.get(clipId)
        if (!cv) { cv = document.createElement('canvas'); captureCanvases.set(clipId, cv) }
        if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h }
        cv.getContext('2d')!.drawImage(frame, 0, 0, w, h)
        setClipFrame(clipId, { source: cv, width: w, height: h })
      }
    }
  }, [clipId])

  const handleImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      setClipFrame(clipId, { source: img, width: img.naturalWidth, height: img.naturalHeight })
    }
  }, [clipId])

  if (hasVideo && videoSrc) {
    // Always use OffthreadVideo — it extracts frames via ffmpeg in a separate
    // thread, reliably fires onVideoFrame in both preview and renderMedia(),
    // and doesn't depend on Chrome's compositor painting hidden elements.
    const VideoComponent = OffthreadVideo
    return (
      <AbsoluteFill>
        <VideoComponent
          src={videoSrc}
          startFrom={startFromFrame}
          playbackRate={clip.speed || 1}
          style={{ width: '100%', height: '100%' }}
          crossOrigin="anonymous"
          volume={volume}
          muted={muted}
          pauseWhenBuffering={pauseOnBuffer}
          acceptableTimeShiftInSeconds={acceptableTimeShiftInSeconds}
          delayRenderTimeoutInMilliseconds={30_000}
          delayRenderRetries={3}
          onVideoFrame={handleVideoFrame}
          onError={isRendering ? undefined : handleVideoError}
        />
      </AbsoluteFill>
    )
  }

  if (isImageOnlyClip(clip) && !posterFailed) {
    const imgSrc = clip.firstFrameUrl || clip.videoUrl
    if (imgSrc) {
      return (
        <AbsoluteFill>
          <Img src={imgSrc} crossOrigin="anonymous" style={{ width: '100%', height: '100%' }} onLoad={handleImgLoad} onError={isRendering ? undefined : handlePosterError} />
        </AbsoluteFill>
      )
    }
  }

  const fallbackSrc = clip.startKeyframeUrl || clip.storyboardImageUrl || clip.firstFrameUrl
  if (fallbackSrc && !posterFailed) {
    return (
      <AbsoluteFill>
        <Img src={fallbackSrc} crossOrigin="anonymous" style={{ width: '100%', height: '100%' }} onLoad={handleImgLoad} onError={isRendering ? undefined : handlePosterError} />
      </AbsoluteFill>
    )
  }

  return (
    <AbsoluteFill style={{ backgroundColor: '#111' }} />
  )
}
